import React from "react";
import { CaretLeft, CaretRight, X, GraduationCap } from "@phosphor-icons/react";

// Five-step guided narrative reinforcing the merged routing + KATLAS story.
// Each step targets a panel id (used to draw a soft highlight).
export const TOUR_STEPS = [
  {
    id: "rwi",
    target: '[data-testid="scenario-panel"]',
    eyebrow: "01 · Scenario evolves",
    title: "A synthetic scenario engine sets the operating context",
    body: "A real-world style event is unfolding. Conditions change, and so do the urgency and sensitivity of the AI work the operation needs.",
  },
  {
    id: "request",
    target: '[data-testid="request-panel"]',
    eyebrow: "02 · AI request triggered",
    title: "An operational request enters the queue",
    body: "Not all requests are equal — some are routine drafting, others touch sensitive cross-boundary action. Each carries an owner role.",
  },
  {
    id: "bloom",
    target: '[data-testid="bloom-panel"]',
    eyebrow: "03 · AI routing layer",
    title: "The routing layer selects a model path",
    body: "Candidate paths are compared on cost, latency, quality and risk. Cheap is fine for low-stakes work; sensitive requests are routed to a risk-aware path.",
  },
  {
    id: "katlas",
    target: '[data-testid="katlas-panel"]',
    eyebrow: "04 · KATLAS · operational governance",
    title: "KATLAS governs the operational consequence",
    body: "Optimisation alone is not enough. KATLAS applies the operational authority check, review threshold, and external handoff policy — and produces an attributable decision record.",
  },
  {
    id: "outcome",
    target: '[data-testid="outcome-panel"]',
    eyebrow: "05 · Outcome & evidence",
    title: "Action under policy, with evidence",
    body: "What actually happened, who was notified, where the handoff went, and the decision record that pins the runtime governance to the action.",
  },
];

export const GuidedTour = ({ open, step, onPrev, onNext, onClose, onJump }) => {
  if (!open) return null;
  const s = TOUR_STEPS[step];
  const total = TOUR_STEPS.length;

  return (
    <>
      {/* Spotlight */}
      <Spotlight target={s.target} />

      {/* Coach card — bottom-center, non-modal so the panel below stays interactive */}
      <div
        data-testid="guided-tour"
        className="fixed left-1/2 bottom-6 -translate-x-1/2 z-[60] w-[520px] max-w-[calc(100vw-2rem)] bg-white border border-stone-300 rounded-sm shadow-xl"
      >
        <div className="flex items-center justify-between px-4 py-2 border-b border-stone-200 bg-stone-50/60">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-stone-500">
            <GraduationCap size={13} />
            Guided walkthrough
          </div>
          <button
            data-testid="guided-tour-close"
            onClick={onClose}
            className="p-1 hover:bg-stone-100 rounded-sm text-stone-500"
          >
            <X size={14} />
          </button>
        </div>
        <div className="px-5 pt-4 pb-3">
          <div className="text-[11px] uppercase tracking-[0.18em] text-stone-500">{s.eyebrow}</div>
          <div className="text-base font-medium text-stone-900 mt-0.5 leading-snug">{s.title}</div>
          <p className="text-sm text-stone-700 mt-1.5 leading-relaxed">{s.body}</p>
        </div>
        <div className="flex items-center justify-between px-4 pb-3">
          <div className="flex items-center gap-1.5">
            {TOUR_STEPS.map((t, i) => (
              <button
                key={t.id}
                onClick={() => onJump(i)}
                aria-label={`Go to step ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? "w-6 bg-stone-900" : "w-1.5 bg-stone-300 hover:bg-stone-500"
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="guided-tour-prev"
              onClick={onPrev}
              disabled={step === 0}
              className="px-2.5 py-1 text-xs flex items-center gap-1 text-stone-600 hover:bg-stone-100 rounded-sm disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <CaretLeft size={11} /> Back
            </button>
            {step < total - 1 ? (
              <button
                data-testid="guided-tour-next"
                onClick={onNext}
                className="px-3 py-1 text-xs font-medium flex items-center gap-1 bg-stone-900 text-white rounded-sm hover:bg-stone-800"
              >
                Next <CaretRight size={11} />
              </button>
            ) : (
              <button
                data-testid="guided-tour-finish"
                onClick={onClose}
                className="px-3 py-1 text-xs font-medium bg-stone-900 text-white rounded-sm hover:bg-stone-800"
              >
                Finish
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Soft spotlight: dim the rest, keep target bright.
const Spotlight = ({ target }) => {
  const [rect, setRect] = React.useState(null);

  React.useEffect(() => {
    if (!target) return;
    const update = () => {
      const el = document.querySelector(target);
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({ x: r.left, y: r.top, w: r.width, h: r.height });
    };
    update();
    const id = window.setInterval(update, 200);
    window.addEventListener("resize", update);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("resize", update);
    };
  }, [target]);

  if (!rect) return null;
  return (
    <div className="pointer-events-none fixed inset-0 z-[55]" aria-hidden="true">
      <div
        className="absolute border-2 border-stone-900/80 rounded-sm transition-all duration-300 shadow-[0_0_0_9999px_rgba(28,25,23,0.32)]"
        style={{ left: rect.x - 4, top: rect.y - 4, width: rect.w + 8, height: rect.h + 8 }}
      />
    </div>
  );
};
